import { t } from 'i18next'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { accessRolesTranslation } from '@/AccessRoles/ui/AccesRolesTranslation'
import { AutoEnrollmentTypes } from '@/AutoEnrollment/domain'
import { NotificationSeverity } from '@/Shared/domain'
import { useMetaResponseHandler } from '@/Shared/ui/form'
import { useLoader } from '@/Shared/ui/loader'
import { useNotification } from '@/Shared/ui/notifications/context/NotificationContext'
import { navigationRoutes } from '@/Shared/ui/routers'
import { UserContract } from '@/User/application'
import {
  MerchantProductModel,
  MerchantsDataModel,
  MultiWebClientMerchantModel,
  UserHirableProductsInfoModel,
  UserModel,
  UserRepository,
  UserType,
} from '@/User/domain'
import {
  CreateProfileFormValues,
  UpdateProfileData,
} from '@/partnersGroups/domain/ProfileTypes'

export interface IUseUserController {
  getUserData: () => Promise<void>
  changeSelectedMerchant: (index: number) => void
  setActiveMultiWebMerchant: (activeMerchantId: number) => Promise<boolean>
  createProfile: (data: CreateProfileFormValues) => Promise<boolean>
  updateProfile: (data: UpdateProfileData) => Promise<boolean>
  deleteProfile: (userNameToDelete: string) => Promise<boolean>
  resendWelcomeEmail: (userNameToResend: string) => Promise<boolean>
  getPermissions: () => Promise<string[]>
  user: UserModel
  merchant: MerchantsDataModel
  userType: UserType | null
  merchantIdentity: { name: string; id: string }
  userName: string
  userProducts: MerchantProductModel[]
  hirableInfo: UserHirableProductsInfoModel
  userMultiWeb: MultiWebClientMerchantModel[]
}

export const useUserController = (repository: UserRepository): IUseUserController => {
  const navigation = useNavigate()
  const { addNotification } = useNotification()
  const resetMerchant = {
    id: 0,
    name: '',
    businessName: '',
    hasFlatRateCommission: false,
    document: '',
    isMasterGroup: false,
    isParent: false,
    isParentCommercial: false,
    isParentWithHardDependency: false,
    isChild: false,
    isChildCommercial: false,
    isChildWithHardDependency: false,
    isIndependent: false,
    isNursery: false,
    products: [],
  }

  const resetUserModel = {
    user: null,
    merchants: [],
    hirableProductsInfo: {
      hasHirableProducts: false,
      canBeHiredOnline: false,
      hirableProducts: [],
    },
  }

  const [selectedMerchant, setSelectedMerchant] = useState<number>(0)

  const [user, setUser] = useState<UserModel>(resetUserModel)
  const [merchant, setMerchant] = useState<MerchantsDataModel>(resetMerchant)

  const [userType, setUserType] = useState<UserType | null>(null)
  const [merchantIdentity, setMerchantIdentity] = useState<{ name: string; id: string }>({
    name: '',
    id: '',
  })
  const [userName, setUserName] = useState<string>('')
  const [userMultiWeb, setUserMultiWeb] = useState<MultiWebClientMerchantModel[]>([])

  const [userProducts, setUserProducts] = useState<MerchantProductModel[]>([])

  const [hirableInfo, setHirableInfo] = useState<UserHirableProductsInfoModel>(
    resetUserModel.hirableProductsInfo,
  )

  const { startLoading, stopLoading } = useLoader()
  const { handleMetaResponse } = useMetaResponseHandler()

  const changeSelectedMerchant = (index: number): void => {
    setSelectedMerchant(index)

    setMerchant(user.merchants[index] ?? resetMerchant)
    setMerchantIdentity({
      name: user.merchants[index]?.businessName ?? '',
      id: user.merchants[index]?.document ?? '',
    })
    setUserProducts(user.merchants[index]?.products ?? [])
  }

  const getUserData = async (): Promise<void> => {
    startLoading()

    const response = await UserContract(repository).getUserData()

    stopLoading()

    if (handleMetaResponse(response?.meta, undefined, { notifySuccess: false })) {
      setUser(response?.data ?? resetUserModel)
      setMerchant(response?.data?.merchants[selectedMerchant] ?? resetMerchant)
      const docId = response?.data?.merchants[selectedMerchant]?.document ?? ''
      const merchantId = response?.data?.merchants[selectedMerchant]?.id ?? ''
      setMerchantIdentity({
        name: response?.data?.merchants[selectedMerchant]?.businessName ?? '',
        id: [docId, merchantId].filter(Boolean).join(' - '),
      })
      setUserName(response?.data?.user?.userName ?? '')
      setUserType(response?.data?.user?.userType ?? null)
      setUserProducts(response?.data?.merchants[selectedMerchant]?.products ?? [])

      setHirableInfo({
        hasHirableProducts:
          response?.data?.hirableProductsInfo?.hasHirableProducts ?? false,
        canBeHiredOnline: response?.data?.hirableProductsInfo?.canBeHiredOnline ?? false,
        hirableProducts: response?.data?.hirableProductsInfo?.hirableProducts ?? [],
      })

      const result = await UserContract(repository).getMultiWebClientMerchants()
      if (handleMetaResponse(result?.meta, undefined, { notifySuccess: false })) {
        setUserMultiWeb(result?.data ?? [])
      }

      if (
        response?.data?.user?.autoEnrollmentType === AutoEnrollmentTypes.autoEnrollment
      ) {
        navigation(navigationRoutes.autoEnrollment)
      }
    }
  }

  const setActiveMultiWebMerchant = async (
    activeMerchantId: number,
  ): Promise<boolean> => {
    if (!activeMerchantId) {
      return false
    }
    startLoading()
    const response = await UserContract(repository).setActiveMultiWeb(activeMerchantId)
    stopLoading()
    const isSuccess =
      handleMetaResponse(response?.meta, undefined, { notifySuccess: false }) &&
      response?.data === true
    if (isSuccess) {
      window.location.reload()
    }
    return isSuccess
  }

  const createProfile = async (data: CreateProfileFormValues): Promise<boolean> => {
    startLoading()
    try {
      const response = await UserContract(repository).createProfile(data)
      const isSuccess =
        handleMetaResponse(
          response?.meta,
          undefined,
          {
            notifySuccess: true,
            successMessage: t(accessRolesTranslation.modal.successMessage),
          },
          t(accessRolesTranslation.modal.errorMessage),
        ) && response?.data === true
      return isSuccess
    } catch (error) {
      addNotification(
        t(accessRolesTranslation.modal.errorMessage),
        NotificationSeverity.error,
      )
      return false
    } finally {
      stopLoading()
    }
  }

  const updateProfile = async (data: UpdateProfileData): Promise<boolean> => {
    startLoading()
    try {
      const response = await UserContract(repository).updateProfile(data)
      const isSuccess =
        handleMetaResponse(
          response?.meta,
          undefined,
          {
            notifySuccess: true,
            successMessage: t(accessRolesTranslation.modal.updateSuccessMessage),
          },
          t(accessRolesTranslation.modal.errorMessage),
        ) && response?.data === true
      return isSuccess
    } catch (error) {
      addNotification(
        t(accessRolesTranslation.modal.errorMessage),
        NotificationSeverity.error,
      )
      return false
    } finally {
      stopLoading()
    }
  }

  const deleteProfile = async (userNameToDelete: string): Promise<boolean> => {
    startLoading()
    try {
      const response = await UserContract(repository).deleteProfile(userNameToDelete)
      const isSuccess = response?.data === true
      isSuccess
        ? addNotification(
            t(accessRolesTranslation.modal.updateSuccessMessage),
            NotificationSeverity.success,
          )
        : addNotification(
            t(accessRolesTranslation.modal.errorMessage),
            NotificationSeverity.error,
          )
      return isSuccess
    } catch (error) {
      addNotification(
        t(accessRolesTranslation.modal.errorMessage),
        NotificationSeverity.error,
      )
      return false
    } finally {
      stopLoading()
    }
  }

  const resendWelcomeEmail = async (userNameToResend: string): Promise<boolean> => {
    startLoading()
    try {
      const response = await UserContract(repository).resendWelcomeEmail(userNameToResend)
      const isSuccess = response?.data === true
      isSuccess
        ? addNotification(
            t(accessRolesTranslation.modal.resendWelcomeEmailSuccessMessage),
            NotificationSeverity.success,
          )
        : addNotification(
            t(accessRolesTranslation.modal.errorMessage),
            NotificationSeverity.error,
          )
      return isSuccess
    } catch (error) {
      addNotification(
        t(accessRolesTranslation.modal.errorMessage),
        NotificationSeverity.error,
      )
      return false
    } finally {
      stopLoading()
    }
  }

  const getPermissions = async (): Promise<string[]> => {
    startLoading()
    const response = await UserContract(repository).getPermissions()
    stopLoading()
    handleMetaResponse(response?.meta, undefined, { notifySuccess: false })
    return response?.data ?? []
  }

  return {
    getUserData,
    changeSelectedMerchant,
    setActiveMultiWebMerchant,
    createProfile,
    updateProfile,
    deleteProfile,
    resendWelcomeEmail,
    getPermissions,
    user,
    merchant,
    userType,
    merchantIdentity,
    userName,
    userProducts,
    hirableInfo,
    userMultiWeb,
  }
}
